import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { AlertTriangle, X } from "lucide-react";
import { usePlatformSettingsPublic } from "../../hooks/usePlatformSettingsPublic";
import "./PlatformMarqueeBanner.scss";

const ALERT_DISMISS_STORAGE = "healbid_platform_site_alert_dismissed";

function alertDismissKey(updatedAt, message) {
  return `v1|${updatedAt || ""}|${message}`;
}

const MarqueeTrack = React.forwardRef(function MarqueeTrack(
  { text, compact = false, copies: copiesProp },
  ref
) {
  const group = (
    <span className="platform-marquee__group">
      <span className="platform-marquee__text">{text}</span>
      <span className="platform-marquee__sep" aria-hidden="true">
        ·
      </span>
    </span>
  );
  let copies = compact ? (copiesProp != null ? copiesProp : 6) : 2;
  if (compact && copies % 2 !== 0) copies += 1;
  return (
    <div
      ref={ref}
      className={`platform-marquee__track${compact ? " platform-marquee__track--compact" : ""}`.trim()}
    >
      {Array.from({ length: copies }, (_, i) => (
        <React.Fragment key={i}>{group}</React.Fragment>
      ))}
    </div>
  );
});

MarqueeTrack.displayName = "MarqueeTrack";

const INLINE_MARQUEE_MAX_COPIES = 42;

/** Grows repeated segments until the track is wide enough to scroll across the full strip (no dead band on the right). */
function PlatformInlineMeasuredMarquee({ text }) {
  const viewportRef = useRef(null);
  const trackRef = useRef(null);
  const [copies, setCopies] = useState(6);

  useLayoutEffect(() => {
    setCopies(6);
  }, [text]);

  useLayoutEffect(() => {
    const vp = viewportRef.current;
    const track = trackRef.current;
    if (!vp || !track) return;
    const vw = vp.clientWidth;
    if (!Number.isFinite(vw) || vw < 48) return;
    const tw = track.scrollWidth;
    if (tw < vw * 2 - 12 && copies < INLINE_MARQUEE_MAX_COPIES) {
      setCopies((c) => Math.min(INLINE_MARQUEE_MAX_COPIES, c + 2));
    }
  }, [text, copies]);

  useEffect(() => {
    const vp = viewportRef.current;
    if (!vp) return;
    let t;
    const scheduleReset = () => {
      clearTimeout(t);
      t = window.setTimeout(() => setCopies(6), 100);
    };
    window.addEventListener("resize", scheduleReset);
    const ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(scheduleReset) : null;
    if (ro) ro.observe(vp);
    return () => {
      clearTimeout(t);
      window.removeEventListener("resize", scheduleReset);
      if (ro) ro.disconnect();
    };
  }, [text]);

  return (
    <div className="platform-inline-marquee" role="status" aria-live="polite">
      <div ref={viewportRef} className="platform-inline-marquee__viewport">
        <MarqueeTrack ref={trackRef} text={text} compact copies={copies} />
      </div>
    </div>
  );
}

function useSiteBanner() {
  const { data } = usePlatformSettingsPublic();
  const siteBanner = data?.siteBanner;
  const updatedAt = data?.updatedAt;

  const marqueeOn = Boolean(siteBanner?.enabled);
  const marqueeText = String(siteBanner?.text || "").trim();
  const showMarquee = marqueeOn && Boolean(marqueeText);

  const alertOn = Boolean(siteBanner?.alertEnabled);
  const alertText = String(siteBanner?.alertText || "").trim();
  const alertVariant = ["info", "warning", "critical"].includes(siteBanner?.alertVariant)
    ? siteBanner.alertVariant
    : "warning";
  const serverAlert = alertOn && Boolean(alertText);
  const alertStorageKey = serverAlert ? alertDismissKey(updatedAt, alertText) : null;

  const [alertDismissed, setAlertDismissed] = useState(() => {
    if (!alertStorageKey) return false;
    try {
      return localStorage.getItem(ALERT_DISMISS_STORAGE) === alertStorageKey;
    } catch (_) {
      return false;
    }
  });

  useEffect(() => {
    if (!alertStorageKey) {
      setAlertDismissed(false);
      return;
    }
    try {
      setAlertDismissed(localStorage.getItem(ALERT_DISMISS_STORAGE) === alertStorageKey);
    } catch (_) {
      setAlertDismissed(false);
    }
  }, [alertStorageKey]);

  const alertVisible = serverAlert && !alertDismissed;
  const pushLayout = siteBanner?.pushLayout !== false;

  const handleAlertDismiss = useCallback(() => {
    if (!alertStorageKey) return;
    try {
      localStorage.setItem(ALERT_DISMISS_STORAGE, alertStorageKey);
    } catch (_) {}
    setAlertDismissed(true);
  }, [alertStorageKey]);

  return {
    showMarquee,
    marqueeText,
    alertVisible,
    alertText,
    alertVariant,
    alertStorageKey,
    handleAlertDismiss,
    pushLayout,
    showAny: showMarquee || alertVisible,
  };
}

/**
 * Compact alert + ticker for the patient app bar (beside HealBid logo). Ignores push/fixed shell options.
 */
export function PlatformSiteBannersInline() {
  const s = useSiteBanner();
  if (!s.showAny) return null;

  return (
    <div className="platform-inline-banners">
      {s.alertVisible ? (
        <div
          className={`platform-inline-alert platform-inline-alert--${s.alertVariant}`}
          role="alert"
          aria-live="assertive"
        >
          <span className="platform-inline-alert__icon" aria-hidden="true">
            <AlertTriangle size={15} strokeWidth={2.25} />
          </span>
          <p className="platform-inline-alert__text" title={s.alertText}>
            {s.alertText}
          </p>
          <button
            type="button"
            className="platform-inline-alert__close"
            onClick={s.handleAlertDismiss}
            aria-label="Dismiss alert"
          >
            <X size={16} strokeWidth={2.25} />
          </button>
        </div>
      ) : null}
      {s.showMarquee ? <PlatformInlineMeasuredMarquee text={s.marqueeText} /> : null}
    </div>
  );
}

/**
 * Full-width admin-controlled alert + strip for provider shell (doctor / hospital).
 */
export default function PlatformMarqueeBanner() {
  const s = useSiteBanner();
  const fixedStackRef = useRef(null);
  const [shimH, setShimH] = useState(0);
  const [layoutTick, setLayoutTick] = useState(0);

  const measureFixedStack = useCallback(() => {
    const el = fixedStackRef.current;
    if (!el) return;
    setShimH(Math.ceil(el.getBoundingClientRect().height));
  }, []);

  useLayoutEffect(() => {
    if (s.pushLayout || !s.showAny) {
      setShimH(0);
      return undefined;
    }
    const el = fixedStackRef.current;
    if (!el) return undefined;
    measureFixedStack();
    const ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(() => measureFixedStack()) : null;
    if (ro) ro.observe(el);
    const onWin = () => measureFixedStack();
    window.addEventListener("resize", onWin);
    return () => {
      if (ro) ro.disconnect();
      window.removeEventListener("resize", onWin);
    };
  }, [s.pushLayout, s.showAny, s.showMarquee, s.alertVisible, measureFixedStack, layoutTick]);

  const handleAlertDismissShell = () => {
    s.handleAlertDismiss();
    setLayoutTick((t) => t + 1);
  };

  if (!s.showAny) return null;

  const marqueeBlock = s.showMarquee ? (
    <div className="platform-marquee" role="status" aria-live="polite">
      <div className="platform-marquee__viewport">
        <MarqueeTrack text={s.marqueeText} />
      </div>
    </div>
  ) : null;

  const alertBlock = s.alertVisible ? (
    <div
      className={`platform-site-alert platform-site-alert--${s.alertVariant}`}
      role="alert"
      aria-live="assertive"
    >
      <span className="platform-site-alert__icon" aria-hidden="true">
        <AlertTriangle size={18} strokeWidth={2.25} />
      </span>
      <p className="platform-site-alert__text">{s.alertText}</p>
      <button
        type="button"
        className="platform-site-alert__close"
        onClick={handleAlertDismissShell}
        aria-label="Dismiss alert"
      >
        <X size={18} strokeWidth={2.25} />
      </button>
    </div>
  ) : null;

  if (s.pushLayout) {
    return (
      <div className="platform-site-banners platform-site-banners--push">
        {alertBlock}
        {marqueeBlock}
      </div>
    );
  }

  return (
    <>
      <div ref={fixedStackRef} className="platform-site-banners platform-site-banners--fixed">
        {alertBlock}
        {marqueeBlock}
      </div>
      <div
        className="platform-site-banners__shim"
        style={{ height: Math.max(shimH, 48) }}
        aria-hidden="true"
      />
    </>
  );
}
